/**
 * Extracts a human-readable string from a value that may be a plain string
 * or a TranslationType object ({ original | en, es, pt, ... }).
 */
function extractMessage(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value
  }
  if (value && typeof value === 'object') {
    const t = value as Record<string, unknown>
    const candidate = t.original ?? t.en ?? t.es ?? t.pt
    if (typeof candidate === 'string') {
      return candidate
    }
  }
  return undefined
}

/**
 * Matches Recurly's "duplicate subscription" validation error, e.g.
 * `1st subscription: Base — You already have a subscription to this plan`.
 *
 * Root cause (FZ-WEB-NEXTJS-H4): a double-submit on /checkout (2-3 purchase
 * clicks for the same plan in <1 minute) creates the subscription on the
 * first request; the follow-up request(s) hit this Recurly validation error.
 * The backend wraps it as a generic `code 5001` with no `transactionError`,
 * so the frontend showed the confusing generic "Try again later" even though
 * the purchase had already succeeded. We detect the underlying Recurly
 * message wherever the backend happens to place it (`extra.info` as a string,
 * or nested under `.message`/`.transactionError.message`/`.params[].message`,
 * mirroring the shapes already handled elsewhere for business errors — see
 * useRecurlyToken.ts's `make3dsError`/`makeTokenError` and
 * SpecialOfferActor's `extra.info.params[0].message` lookup) rather than
 * relying on a single fixed path.
 */
export const ALREADY_SUBSCRIBED_PATTERN = /already have a subscription to this plan/i

function findAlreadySubscribedMessage(apiError: Record<string, unknown>): string | undefined {
  const info = (apiError.extra as Record<string, unknown> | undefined)?.info

  const candidates: unknown[] = [
    info,
    (info as Record<string, unknown> | undefined)?.message,
    (info as Record<string, unknown> | undefined)?.transactionError,
    ((info as Record<string, unknown> | undefined)?.transactionError as Record<string, unknown> | undefined)?.message,
    ...(Array.isArray((info as Record<string, unknown> | undefined)?.params)
      ? ((info as Record<string, unknown>).params as Array<Record<string, unknown>>).map((p) => p?.message)
      : []),
    apiError.description,
    apiError.message,
  ]

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && ALREADY_SUBSCRIBED_PATTERN.test(candidate)) {
      return candidate
    }
  }
  return undefined
}

/**
 * Friendly, translated message shown to the user in place of the generic
 * "Try again later" when the backend's error is actually Recurly refusing a
 * duplicate subscription to the same plan (the purchase already succeeded on
 * a prior click).
 */
const ALREADY_SUBSCRIBED_USER_MESSAGE = {
  original: 'You already have an active subscription to this plan.',
  en: 'You already have an active subscription to this plan.',
  es: 'Ya tenés una suscripción activa a este plan.',
  pt: 'Você já tem uma assinatura ativa para este plano.',
}

/**
 * Recurly `transactionError.category` values that come with a specific,
 * actionable message (`transactionError.message` / `.merchantAdvice`) rather
 * than a generic one:
 *   - 'hard': issuer permanently refused the transaction, retrying the same
 *     card will not help (see FZ-WEB-NEXTJS-H4).
 *   - 'fraud': gateway's own fraud/velocity screening blocked the charge
 *     (see FZ-WEB-NEXTJS-SB) — e.g. "high-volume from a single source,
 *     indicating possible fraudulent activity". Not card-specific, but still
 *     a concrete, user-actionable reason.
 * For both, `toApiError` was always preferring the backend's generic
 * `userMessage` ("Validation Errors" / "Try again later") over it, so users
 * never saw the actionable reason.
 */
const ACTIONABLE_DECLINE_CATEGORIES = new Set(['hard', 'fraud'])

function findHardDeclineMessage(apiError: Record<string, unknown>): string | undefined {
  const info = (apiError.extra as Record<string, unknown> | undefined)?.info
  const transactionError = (info as Record<string, unknown> | undefined)?.transactionError as
    | Record<string, unknown>
    | undefined

  if (!transactionError || !ACTIONABLE_DECLINE_CATEGORIES.has(transactionError.category as string)) {
    return undefined
  }

  return (
    extractMessage(transactionError.message) ||
    extractMessage(transactionError.merchantAdvice)
  )
}

/**
 * Converts a plain API error object into a proper Error instance,
 * preserving all original fields (userMessage, code, extra, etc.)
 * so downstream consumers can still access them.
 *
 * `userMessage` is a TranslationType (often an object, not a string), so we
 * resolve it to a real string before building the Error — otherwise the
 * message stringifies to "[object Object]" in Sentry.
 *
 * When the underlying cause is Recurly's "already have a subscription to
 * this plan" validation error (double-submit, see FZ-WEB-NEXTJS-H4), we
 * override `userMessage` with a clear, translated message and tag the error
 * with `classification: 'already_subscribed'` — following the same pattern
 * `make3dsError`/`makeTokenError` (useRecurlyToken.ts) use to preserve a
 * `code`/`classification` so the checkout classifier (classifyCheckoutError)
 * can tell this apart from a real gateway fault.
 *
 * @example
 * const error = toApiError(responseJson.error)
 * throw error // proper Error with stack trace, Sentry-friendly
 */
export function toApiError(apiError: Record<string, unknown>): Error {
  const alreadySubscribedMessage = findAlreadySubscribedMessage(apiError)

  if (alreadySubscribedMessage) {
    return Object.assign(new Error(`Recurly: duplicate subscription attempt — ${alreadySubscribedMessage}`), {
      ...apiError,
      userMessage: ALREADY_SUBSCRIBED_USER_MESSAGE,
      classification: 'already_subscribed',
    })
  }

  const hardDeclineMessage = findHardDeclineMessage(apiError)

  if (hardDeclineMessage) {
    return Object.assign(new Error(`Recurly: actionable decline — ${hardDeclineMessage}`), {
      ...apiError,
      userMessage: {
        original: hardDeclineMessage,
        en: hardDeclineMessage,
        es: hardDeclineMessage,
        pt: hardDeclineMessage,
      },
      classification: 'actionable_decline',
    })
  }

  const message =
    extractMessage(apiError.userMessage) ||
    extractMessage(apiError.message) ||
    extractMessage(apiError.description) ||
    'Unknown API error'
  return Object.assign(new Error(message), apiError)
}
